import React from 'react';
import { Phone, Mail, MessageCircle, Linkedin, Clock } from 'lucide-react';

interface RecentLeadItemProps {
  lead: any;
}

const RecentLeadItem: React.FC<RecentLeadItemProps> = ({ lead }) => {
  const getInitials = (name: string) => {
    if (!name) return '?';
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getChannelIcon = (channel: string) => {
    const normalizedChannel = (channel || '').toLowerCase().trim();
    
    switch (normalizedChannel) {
      case 'email':
        return <Mail size={12} className="text-red-600" />;
      case 'call':
        return <Phone size={12} className="text-blue-600" />;
      case 'whatsapp':
        return <MessageCircle size={12} className="text-green-600" />;
      case 'linkedin':
        return <Linkedin size={12} className="text-blue-600" />;
      default:
        return <MessageCircle size={12} className="text-gray-600" />;
    }
  };

  const getChannelTag = (channel: string) => {
    const normalizedChannel = (channel || '').toLowerCase().trim();
    
    switch (normalizedChannel) {
      case 'email':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 border border-red-200">
            <Mail size={10} className="mr-1" />
            Email
          </span>
        );
      case 'call':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
            <Phone size={10} className="mr-1" />
            Call
          </span>
        );
      case 'whatsapp':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
            <MessageCircle size={10} className="mr-1" />
            WhatsApp
          </span>
        );
      case 'linkedin':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
            <Linkedin size={10} className="mr-1" />
            LinkedIn
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 border border-gray-200">
            <MessageCircle size={10} className="mr-1" />
            Unknown
          </span>
        );
    }
  };

  const getStatusTag = (status: string) => {
    const normalizedStatus = (status || '').toLowerCase().trim();
    
    switch (normalizedStatus) {
      case 'replied':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
            Replied
          </span>
        );
      case 'booked meeting':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 border border-purple-200">
            Booked Meeting
          </span>
        );
      case 'opened':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-200">
            Opened
          </span>
        );
      case 'connected':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
            Connected
          </span>
        );
      case 'interested':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800 border border-orange-200">
            Interested
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 border border-gray-200">
            {status || 'Unknown'}
          </span>
        );
    }
  };

  const formatTimeAgo = (timestamp: string) => {
    if (!timestamp || timestamp === 'N/A' || timestamp.toString().trim() === '' || timestamp === 'undefined') return 'N/A';
    
    try {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) return 'N/A';
      
      const now = new Date();
      const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
      
      if (diffInHours < 1) return 'Just now';
      if (diffInHours < 24) return `${diffInHours}h ago`;
      
      const diffInDays = Math.floor(diffInHours / 24);
      return `${diffInDays}d ago`;
    } catch {
      return 'N/A';
    }
  };

  const initials = getInitials(lead.dm_full_name || lead.contact_person);
  const timeAgo = formatTimeAgo(lead.last_reply_timestamp);

  return (
    <div className="flex items-center space-x-3 p-3 hover:bg-gray-50 rounded-lg transition-colors">
      {/* Avatar */}
      <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-medium text-sm flex-shrink-0">
        {initials}
      </div>

      {/* Lead Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-1">
          <h4 className="text-sm font-medium text-gray-900 truncate">
            {lead.dm_full_name || lead.contact_person || 'Unknown'}
          </h4>
          <span className="text-xs text-gray-500 ml-2 flex-shrink-0">
            {timeAgo}
          </span>
        </div>
        
        <p className="text-xs text-gray-600 truncate mb-2">
          {lead.company_name || 'Unknown Company'}
        </p>

        <div className="flex items-center space-x-2">
          {getChannelTag(lead.channel)}
          {getStatusTag(lead.lead_status || lead.status)}
        </div>
      </div>
    </div>
  );
};

export default RecentLeadItem;